import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { t, errorText, relativePast } from '../i18n/index.ts';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { Avatar, ErrorView, Field, Icon, Loading, useToast } from '../components/ui.tsx';
import { MfaSetup } from './Auth.tsx';
import { formatPhone } from './Debts.tsx';
import { useAmountUnit } from '../lib/amounts.ts';
import { playYourTurn, useSoundSetting } from '../lib/sound.ts';
import { NotificationSettings } from './NotificationSettings.tsx';import { ThemeSettings } from '../components/ThemePicker.tsx';




/** "Chrome, Windows" from a browser's user-agent string (unknown parts are left out). */
export function deviceName(ua: string | null): string {
  if (!ua) return t('profile.unknownDevice');
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : null;
  const os = /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : null;
  return [browser, os].filter(Boolean).join(', ') || t('profile.unknownDevice');
}
export function ProfilePage() {
  const { me, refresh } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [phone, setPhone] = useState(me?.phone ? formatPhone(me.phone) : '');
  const [newEmail, setNewEmail] = useState('');
  const [emailSent, setEmailSent] = useState<{ to: string; devLink?: string } | null>(null);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [unit, setUnit] = useAmountUnit();
  const [soundsOn, setSounds] = useSoundSetting();
  const [allSessions, setAllSessions] = useState(false);
  const home = useQuery({ queryKey: ['home'], queryFn: () => api.get<{ unreadNotifications: number }>('/api/home'), enabled: false });
  const unreadNotes = home.data?.unreadNotifications ?? 0;
  const logout = async () => {
    await api.post('/api/auth/logout').catch(() => undefined);
    qc.clear();
    window.location.href = '/kirjaudu';
  };
  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<{ sessions: { id: string; lastUsedAt: number; userAgent: string | null; ip: string | null; current: boolean }[] }>('/api/me/sessions'),
  });
  const done = async (msg?: string) => {
    await refresh();
    if (msg) toast(msg);
  };
  const patch = useMutation({
    mutationFn: (body: unknown) => api.patch('/api/me', body),
    onError: (e) => toast(errorText(e), 'error'),
  });
  if (!me) return <Loading />;

  const uploadAvatar = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    try {
      await api.upload('/api/me/avatar', form);
      await done(t('app.saved'));
    } catch (err) {
      toast(errorText(err), 'error');
    }
  };

  return (
    <div className="page-narrow">
      <h1>{t('profile.title')}</h1>

      {/* Phones have no side menu: notifications, admin and logout are reached from here. */}
      <nav className="panel mobile-hub" aria-label={t('profile.moreLabel')}>
        <Link className="hub-link" to="/tilastot">
          <Icon name="chart" />
          <span>{t('nav.stats')}</span>
        </Link>
        <Link className="hub-link" to="/ilmoitukset">
          <Icon name="bell" />
          <span>{t('nav.notifications')}</span>
          {unreadNotes > 0 && <span className="count">{unreadNotes}</span>}
        </Link>
        {me.role === 'superadmin' && (
          <Link className="hub-link" to="/yllapito">
            <Icon name="shield" />
            <span>{t('nav.admin')}</span>
          </Link>
        )}
        <button type="button" className="hub-link danger" onClick={logout}>
          <Icon name="logout" />
          <span>{t('nav.logout')}</span>
        </button>
      </nav>

      <section className="panel profile-card" aria-labelledby="avatar-h">
        <div className="profile-id">
          <Avatar name={me.displayName} url={me.avatarUrl} size="l" />
          <div className="profile-id-text">
            <h2 id="avatar-h">{me.displayName}</h2>
            <span className="hint">{me.email}</span>
          </div>
        </div>
        <div>
          <div>
            <p className="hint">{t('profile.avatarHelp')}</p>
            <div className="btn-row">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={(e) => e.target.files?.[0] && uploadAvatar(e.target.files[0])} />
              <button className="btn btn-small" onClick={() => fileRef.current?.click()}>
                {t('profile.upload')}
              </button>
              {me.avatarUrl && (
                <button
                  className="btn btn-small btn-ghost"
                  onClick={async () => {
                    try {
                      await api.del('/api/me/avatar');
                    } catch (err) {
                      toast(errorText(err), 'error');
                    }
                    await done();
                  }}
                >
                  {t('profile.remove')}
                </button>
              )}
            </div>
            <p className="hint" style={{ marginTop: 8 }}>
              {t('profile.displayNameHelp')}
            </p>
          </div>
        </div>
      </section>

      <section className="panel" aria-labelledby="phone-h">
        <h2 id="phone-h">{t('profile.phoneTitle')}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            patch.mutate({ phone: phone.trim() || null }, { onSuccess: () => void done(t('profile.phoneSaved')) });
          }}
        >
          <Field label={t('profile.phoneLabel')} hint={t('profile.phoneHelp')} htmlFor="phone">
            <input id="phone" className="input" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="040 123 4567" />
          </Field>
          <div className="btn-row">
            <button className="btn btn-primary" disabled={patch.isPending}>
              {t('app.save')}
            </button>
            {me.phone && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setPhone('');
                  patch.mutate({ phone: null }, { onSuccess: () => void done(t('app.saved')) });
                }}
              >
                {t('profile.phoneRemove')}
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="panel" aria-labelledby="display-h">
        <h2 id="display-h">{t('profile.displayTitle')}</h2>
        <ThemeSettings />
        <fieldset style={{ border: 0, padding: 0, margin: 0, background: 'none' }}>
          <legend className="label" style={{ padding: 0 }}>
            {t('profile.amountUnit')}
          </legend>
          <p className="hint">{t('profile.amountUnitHelp')}</p>
          <div className="radio-row">
            {(['chips', 'bb'] as const).map((u) => (
              <label key={u} className="check">
                <input type="radio" name="amount-unit" checked={unit === u} onChange={() => void setUnit(u).then(() => toast(t('app.saved')))} />
                <span>{u === 'chips' ? t('profile.amountChips') : t('profile.amountBb')}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className="sound-toggle">
          <span>
            <span className="label">{t('profile.sounds')}</span>
            <span className="hint">{t('profile.soundsHelp')}</span>
          </span>
          <span className="switch">
            <input type="checkbox" role="switch" checked={soundsOn} onChange={(e) => void setSounds(e.target.checked).then(() => e.target.checked && playYourTurn())} />
            <span className="switch-track" aria-hidden="true" />
          </span>
        </label>
      </section>

      <NotificationSettings />

      <section className="panel" aria-labelledby="email-h">
        <h2 id="email-h">{t('profile.emailTitle')}</h2>
        <p>{me.email}</p>
        {emailSent ? (
          <div className="alert alert-ok" role="status">
            <p style={{ margin: 0 }}>{t('profile.emailLinkSent', { email: emailSent.to })}</p>
            {emailSent.devLink && (
              <a className="btn btn-small" style={{ marginTop: 8 }} href={emailSent.devLink}>
                {t('auth.devLinkOpen')}
              </a>
            )}
          </div>
        ) : (
          <form
            onSubmit={async (e: FormEvent) => {
              e.preventDefault();
              try {
                const res = await api.post<{ sent: true; devLink?: string }>('/api/me/email', { newEmail });
                setEmailSent({ to: newEmail.trim(), devLink: res.devLink });
                setNewEmail('');
              } catch (err) {
                toast(errorText(err), 'error');
              }
            }}
          >
            <Field label={t('profile.newEmail')} hint={t('profile.newEmailHelp')} htmlFor="new-email">
              <input id="new-email" className="input" type="email" autoComplete="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            </Field>
            <button className="btn" disabled={!newEmail.includes('@')}>
              {t('profile.changeEmail')}
            </button>
          </form>
        )}
      </section>

      <section className="panel" aria-labelledby="mfa-h">
        <h2 id="mfa-h">{t('profile.mfaTitle')}</h2>
        {me.mfaEnabled ? (
          <p>{t('profile.mfaOn')}</p>
        ) : mfaOpen ? (
          <MfaSetup onDone={() => done(t('app.saved'))} next="/profiili" />
        ) : (
          <>
            <p className="hint">{t('profile.mfaOff')}</p>
            <button className="btn" onClick={() => setMfaOpen(true)}>
              {t('auth.mfaEnable')}
            </button>
          </>
        )}
      </section>

      <section className="panel" aria-labelledby="sessions-h">
        <div className="section-head">
          <h2 id="sessions-h">{t('profile.sessionsTitle')}</h2>
          <button
            className="btn btn-small"
            onClick={async () => {
              await api.post('/api/me/sessions/revoke-others');
              void sessions.refetch();
            }}
          >
            {t('profile.revokeOthers')}
          </button>
        </div>
        {sessions.isPending && <Loading />}
        {sessions.isError && <ErrorView error={sessions.error} />}
        <ul className="rows">
          {(sessions.data?.sessions ?? [])
            .filter((s, i) => allSessions || s.current || i < 4)
            .map((s) => (
            <li key={s.id} className="row">
              <div className="row-main">
                <div className="row-title" style={{ fontWeight: 600 }}>
                  {s.current ? t('profile.thisDevice') : deviceName(s.userAgent)}
                </div>
                <div className="row-sub">{t('profile.lastUsed', { time: relativePast(s.lastUsedAt) })}</div>
              </div>
              {!s.current && (
                <button
                  className="btn btn-small"
                  aria-label={`${t('profile.revoke')}: ${deviceName(s.userAgent)}`}
                  onClick={async () => {
                    try {
                      await api.del(`/api/me/sessions/${s.id}`);
                    } catch (err) {
                      toast(errorText(err), 'error');
                    }
                    void sessions.refetch();
                  }}
                >
                  {t('profile.revoke')}
                </button>
              )}
            </li>
          ))}
        </ul>
        {!allSessions && (sessions.data?.sessions.length ?? 0) > 4 && (
          <button type="button" className="btn btn-small" style={{ marginTop: 8 }} onClick={() => setAllSessions(true)}>
            {t('profile.showAllSessions', { n: sessions.data!.sessions.length })}
          </button>
        )}
      </section>
    </div>
  );
}
